import type { GameState, PlayerState } from '../shared/types';
import type { Terrain } from '../shared/terrain';
import { turnKey } from './turn-transition';
import { ORIGINAL_ACTION_TICK_MS } from '../shared/attack-timing';
import { sourceFallY, sourceWalk, type WalkFall } from '../shared/walk-geometry';

interface Pose { x: number; y: number; face: -1 | 1 }
interface PendingMove { seq: number; dx: number; at: number }
interface Track {
  authoritative: PlayerState;
  predicted: PlayerState;
  from: Pose;
  target: Pose;
  started: number;
  duration: number;
  walkingUntil: number;
  pending: PendingMove[];
  fall: (WalkFall & { started: number }) | null;
}

export interface MovementFrame extends Pose { walking: boolean; falling: boolean; pendingCommands: number; hpBeforeDeath?: number }
const pose = ({ x, y, face }: PlayerState | Pose): Pose => ({ x, y, face });

/** Same per-pixel ground checks as the authority; no state/HP is sent back. */
export function predictWalk(player: PlayerState, dx: number, terrain: Terrain): PlayerState {
  return sourceWalk(player, dx, terrain).player;
}

/**
 * Replays only unacknowledged movement over each authoritative snapshot.
 * Rendering never waits for a network round trip; collision/energy remain
 * server-owned, and an explicit ack also handles blocked/reversed movements.
 */
export class MovementPresentation {
  private tracks = new Map<string, Track>();
  private round = '';
  private currentTurn = '';
  private terrain: Terrain | null = null;
  private serverEpochOffset = 0;

  get hasTerrain() { return Boolean(this.terrain); }
  clearTerrain(): void { this.terrain = null; }

  setTerrain(terrain: Terrain, now = performance.now()): void {
    this.terrain = terrain;
    for (const track of this.tracks.values()) this.reconcile(track, now);
  }

  observe(state: GameState, ackSeq?: number, now = performance.now(), serverNow = Date.now()): void {
    this.serverEpochOffset = serverNow - now;
    if (state.phase === 'lobby') { this.reset(); return; }
    const round = `${state.round}:${state.mapId}`;
    if (round !== this.round) { this.tracks.clear(); this.round = round; }
    const nextTurn = turnKey(state);
    const changedTurn = Boolean(this.currentTurn && nextTurn !== this.currentTurn);
    this.currentTurn = nextTurn;
    for (const player of state.players) {
      let track = this.tracks.get(player.id);
      if (!track) {
        track = { authoritative: { ...player }, predicted: { ...player }, from: pose(player), target: pose(player), started: now, duration: 0, walkingUntil: 0, pending: [], fall: null };
        this.tracks.set(player.id, track);
        this.reconcile(track, now);
      } else {
        track.authoritative = { ...player };
        const retention = Math.max(1800, track.fall ? track.fall.duration + 1200 : 0);
        track.pending = changedTurn || !player.alive ? [] : track.pending.filter((move) => (ackSeq === undefined || move.seq > ackSeq) && now - move.at < retention);
        this.reconcile(track, now);
      }
    }
    for (const id of this.tracks.keys()) if (!state.players.some((player) => player.id === id)) this.tracks.delete(id);
  }

  predict(playerId: string, dx: number, seq: number, now = performance.now()): void {
    const track = this.tracks.get(playerId);
    if (!track || !Number.isSafeInteger(seq) || !Number.isInteger(dx) || Math.abs(dx) > 32 || !dx) return;
    if (!track.predicted.alive || track.predicted.energy <= 0 || track.pending.length >= 24 || this.isFalling(playerId, now)) return;
    track.pending.push({ seq, dx, at: now });
    track.walkingUntil = now + 130;
    this.reconcile(track, now, ORIGINAL_ACTION_TICK_MS);
  }

  cancel(playerId?: string, now = performance.now()): void {
    for (const [id, track] of this.tracks) if (!playerId || id === playerId) {
      track.pending = []; this.reconcile(track, now, 100);
    }
  }

  sample(playerId: string, now = performance.now()): MovementFrame | null {
    const track = this.tracks.get(playerId);
    if (!track) return null;
    const t = track.duration ? Math.max(0, Math.min(1, (now - track.started) / track.duration)) : 1;
    if (track.fall) {
      const elapsed = Math.max(0, now - track.fall.started), falling = elapsed < track.fall.duration;
      return { x: track.fall.to.x, y: sourceFallY(track.fall.from.y, track.fall.to.y, elapsed), face: track.target.face,
        walking: false, falling, pendingCommands: track.pending.length,
        ...(falling && track.fall.hpBeforeDeath !== undefined ? { hpBeforeDeath: track.fall.hpBeforeDeath } : {}) };
    }
    return {
      x: track.from.x + (track.target.x - track.from.x) * t,
      y: track.from.y + (track.target.y - track.from.y) * t,
      face: track.target.face,
      walking: now < track.walkingUntil || (t < 1 && Math.abs(track.from.x - track.target.x) > .2),
      falling: false,
      pendingCommands: track.pending.length,
    };
  }

  private reconcile(track: Track, now: number, duration = 100): void {
    let predicted = { ...track.authoritative };
    let fall: (WalkFall & { started: number }) | null = null;
    const authoritativeFall = predicted.movementFall;
    if (authoritativeFall && (predicted.alive || authoritativeFall.hpBeforeDeath !== undefined)
      && authoritativeFall.startedAt + authoritativeFall.duration > now + this.serverEpochOffset) {
      fall = { from: authoritativeFall.from, to: authoritativeFall.to, duration: authoritativeFall.duration,
        started: authoritativeFall.startedAt - this.serverEpochOffset, hpBeforeDeath: authoritativeFall.hpBeforeDeath };
    }
    for (const move of track.pending) {
      if (fall) {
        if (move.at < fall.started + fall.duration) break;
        fall = null;
      }
      if (this.terrain) {
        const walked = sourceWalk(predicted, move.dx, this.terrain); predicted = walked.player;
        if (walked.fall) fall = { ...walked.fall, started: move.at };
      }
      else predicted.face = move.dx < 0 ? -1 : 1;
    }
    track.predicted = predicted;
    const target = pose(predicted);
    if (fall) {
      const existing = track.fall;
      // Local prediction already began at key input. Confirming the same
      // source action must not rewind it to the packet's later start epoch.
      if (existing && existing.to.x === fall.to.x && existing.to.y === fall.to.y
        && existing.from.y === fall.from.y) fall.started = Math.min(existing.started, fall.started);
      track.fall = fall; track.target = target; track.walkingUntil = 0; return;
    }
    // Retain a completed fall until a new pose is requested. Stale snapshots
    // and acknowledgements of earlier steps cannot restart its visual clock.
    if (track.fall && now - track.fall.started < track.fall.duration && predicted.alive
      && target.x === track.fall.to.x && target.y === track.fall.to.y) return;
    const current = this.sample(track.authoritative.id, now)!;
    if (!predicted.alive || target.x !== track.target.x || target.y !== track.target.y) track.fall = null;
    if (target.x === track.target.x && target.y === track.target.y) { track.target.face = target.face; return; }
    const distance = Math.hypot(target.x - current.x, target.y - current.y);
    track.from = pose(current); track.target = target;
    track.started = now; track.duration = distance > 150 ? 0 : duration;
    if (Math.abs(target.x - current.x) > .2) track.walkingUntil = now + duration + 70;
  }

  get debug() {
    return [...this.tracks].map(([playerId, track]) => ({ playerId, authoritative: pose(track.authoritative), predicted: pose(track.predicted),
      fall: track.fall ? { ...track.fall, from: { ...track.fall.from }, to: { ...track.fall.to } } : null,
      pending: track.pending.map(({ seq, dx }) => ({ seq, dx })) }));
  }

  isFalling(playerId: string, now = performance.now()): boolean {
    const fall = this.tracks.get(playerId)?.fall;
    return Boolean(fall && now - fall.started < fall.duration);
  }

  anyFalling(now = performance.now()): boolean {
    for (const track of this.tracks.values()) if (track.fall && now - track.fall.started < track.fall.duration) return true;
    return false;
  }

  deathStartedAt(playerId: string): number | null {
    const fall = this.tracks.get(playerId)?.fall;
    return fall?.hpBeforeDeath === undefined ? null : fall.started + fall.duration;
  }

  reset(): void { this.tracks.clear(); this.round = ''; this.currentTurn = ''; this.terrain = null; }
}
